import React from 'react';
import { format as formatDate } from 'date-fns';
import { omit, keys, isEmpty, keyBy } from 'lodash';
import numeral from 'numeral';

import { fieldDisplayValue } from '../shared/util';
import { answerDisplayValue } from '../util';
import {
  statuses as troubleInquiryStatuses,
  fields as troubleInquiryFieldsFn,
  basicFields as troubleInquiryBasicFields,
  shipmentFields as troubleInquiryShipmentFields,
  reimbursementResultFields as troubleInquiryReimbursementResultFields,
  discountFields as troubleInquiryDiscountFields,
} from '../shared/models/troubleInquiry';
import {
  statuses as inquiryStatuses,
  fields as inquiryFieldsFn,
  shipmentFields as inquiryShipmentFields,
  reimbursementResultFields as inquiryReimbursementResultFields,
} from '../shared/models/inquiry';
import {
  statuses as methodInquiryStatuses,
  fields as methodInquiryFieldsFn,
  basicFields as methodInquiryBasicFields,
  shipmentFields as methodInquiryShipmentFields,
} from '../shared/models/methodInquiry';
import { ordererFields, destinationFields } from '../shared/models/order';

const { entries } = Object;
const thWidth = 220;
const personalInfoFieldNames = [...keys(ordererFields()), ...keys(destinationFields())];

const formatTimestamp = (ts) => {
  if (!ts) return '';
  if (ts.toDate) return formatDate(ts.toDate(), 'yyyy/MM/dd HH:mm:ss');
  if (ts._seconds) return formatDate(new Date(ts._seconds * 1000), 'yyyy/MM/dd HH:mm:ss');
  return '';
};

function FieldRow({ fieldName, fieldSetting, value, data, renderValue, hidePersonalInfo }) {
  const isPersonal = personalInfoFieldNames.includes(fieldName);
  if (isPersonal && hidePersonalInfo) return null;
  const label = keys(destinationFields()).includes(fieldName)
    ? `発送先 - ${fieldSetting.label}`
    : fieldSetting.label;
  const displayValue = fieldDisplayValue(value, { values: data, ...fieldSetting });
  return (
    <tr className={isPersonal ? 'personal-info' : undefined}>
      <th style={{ width: thWidth }}>{label}</th>
      <td className="text-break" style={{ whiteSpace: 'pre-line' }}>
        {renderValue ? renderValue(displayValue) : displayValue}
      </td>
    </tr>
  );
}

export function TroubleInquiryDetailRows({
  inquiry,
  productsById,
  productTypes,
  products,
  country,
  relatedQuestions = [],
  renderNameValue,
  hidePersonalInfo,
  extraRows,
}) {
  const { label: statusLabel, color } = troubleInquiryStatuses[inquiry.status] || {};
  const fields = { ...omit(troubleInquiryFieldsFn({ country, productTypes, products }), ['destinationType']), ...troubleInquiryShipmentFields({ country }) };
  return (
    <>
      <tr>
        <th style={{ width: thWidth }}>ID</th>
        <td>{inquiry.id}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>状態</th>
        <td><span className={`badge badge-${color || 'secondary'}`}>{statusLabel}</span></td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>パーツ</th>
        <td>
          {(inquiry.items || []).map((item, i) => {
            const { productId, quantity } = item;
            const product = productsById?.[productId];
            return (
              product != null && (
                <div key={i}>
                  {inquiry.status !== 'supported' &&
                    !inquiry.shipmentRequested &&
                    quantity > product.normalInventory && (
                      <span className="mr-1 text-danger small">[在庫不足]</span>
                    )}
                  <span>{product.code}</span>
                  <span className="ml-2">{product.name}</span>
                  <span className="ml-2">{numeral(quantity).format('0,0')}個</span>
                </div>
              )
            );
          })}
        </td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>合計金額(税込)</th>
        <td className="text-right">{numeral(inquiry.amount).format('0,0')}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>お問合せ日時</th>
        <td>{formatTimestamp(inquiry.createdAt)}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>元受注ID</th>
        <td>{inquiry.sourceOrder?.id}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>元受注日時</th>
        <td>{inquiry.sourceOrder != null && formatTimestamp(inquiry.sourceOrder.createdAt)}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>担当者</th>
        <td>{inquiry.respondedBy?.displayName}</td>
      </tr>
      {extraRows}
      {entries(troubleInquiryBasicFields({ country, productTypes, products })).map(([fieldName, fieldSetting]) => {
        const displayValue = fieldDisplayValue(inquiry[fieldName], fieldSetting);
        const product = fieldName === 'productId' ? productsById?.[inquiry[fieldName]] : null;
        const displayText = product ? `[${product.code}] ${product.name}` : displayValue;
        return (
          <tr key={fieldName}>
            <th style={{ width: thWidth }}>{fieldSetting.label}</th>
            <td className="text-break" style={{ whiteSpace: 'pre-line' }}>{displayText}</td>
          </tr>
        );
      })}
      {relatedQuestions.map((question) => {
        const { id, type, name } = question;
        const answer = inquiry.answers?.[id];
        const displayValue = {
          text: answer,
          checkbox: keys(answer || {}).join(', '),
          radio: answer,
          select: answer,
          imageFile: [answer].flat().map((a, i) => (
            <div key={i}><a href={a?.url} target="_blank">{a?.name}</a></div>
          )),
        }[type];
        return (
          <tr key={id}>
            <th style={{ width: thWidth }}>{name}</th>
            <td>{displayValue}</td>
          </tr>
        );
      })}
      {entries(omit(fields, keys(troubleInquiryBasicFields({ country })))).map(([fieldName, fieldSetting]) => (
        <FieldRow
          key={fieldName}
          fieldName={fieldName}
          fieldSetting={fieldSetting}
          value={inquiry[fieldName]}
          data={inquiry}
          renderValue={fieldName === 'name' && renderNameValue ? renderNameValue : undefined}
          hidePersonalInfo={hidePersonalInfo}
        />
      ))}
      {entries(troubleInquiryReimbursementResultFields()).map(([fieldName, fieldSetting]) => (
        <tr key={fieldName}>
          <th style={{ width: thWidth }}>{fieldSetting.label}</th>
          <td className="text-break" style={{ whiteSpace: 'pre-line' }}>
            {fieldDisplayValue(inquiry.reimbursementResult?.[fieldName], fieldSetting)}
          </td>
        </tr>
      ))}
      {entries(troubleInquiryDiscountFields()).map(([fieldName, fieldSetting]) => (
        <tr key={fieldName}>
          <th style={{ width: thWidth }}>{fieldSetting.label}</th>
          <td className="text-break" style={{ whiteSpace: 'pre-line' }}>
            {fieldDisplayValue(inquiry.discount?.[fieldName], fieldSetting)}
          </td>
        </tr>
      ))}
    </>
  );
}

export function InquiryDetailRows({
  inquiry,
  productsById,
  fields,
  country,
  renderNameValue,
  hidePersonalInfo,
  extraRows,
}) {
  const { label: statusLabel, color } = inquiryStatuses[inquiry.status] || {};
  return (
    <>
      <tr>
        <th style={{ width: thWidth }}>ID</th>
        <td>
          <div>
            {inquiry.id}
            {inquiry.isAdminCreated && <span className="badge badge-info ml-1">管理者作成</span>}
          </div>
        </td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>状態</th>
        <td><span className={`badge badge-${color || 'secondary'}`}>{statusLabel}</span></td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>担当者</th>
        <td>{inquiry.respondedBy?.displayName}</td>
      </tr>
      {extraRows}
      {!isEmpty(inquiry.items) && (
        <tr>
          <th style={{ width: thWidth }}>パーツ</th>
          <td>
            {inquiry.items?.map((item, i) => {
              const { productId, quantity } = item;
              const product = productsById?.[productId];
              return (
                product != null && (
                  <div key={i}>
                    {inquiry.status !== 'supported' &&
                      !inquiry.shipmentRequested &&
                      quantity > product.normalInventory && (
                        <span className="mr-1 text-danger small">[在庫不足]</span>
                      )}
                    <span>{product.code}</span>
                    <span className="ml-2">{product.name}</span>
                    <span className="ml-2">{numeral(quantity).format('0,0')}個</span>
                  </div>
                )
              );
            })}
          </td>
        </tr>
      )}
      <tr>
        <th style={{ width: thWidth }}>ファイル</th>
        <td>
          {(inquiry.files || []).map(({ name, url }, i) => (
            <div key={i}><a href={url} target="_blank">{name}</a></div>
          ))}
        </td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>お問合せ日時</th>
        <td>{formatTimestamp(inquiry.createdAt)}</td>
      </tr>
      {entries(fields)
        .filter(([fieldName, { hidden = (_) => false }]) => !hidden(inquiry))
        .map(([fieldName, fieldSetting]) => (
        <FieldRow
          key={fieldName}
          fieldName={fieldName}
          fieldSetting={fieldSetting}
          value={inquiry[fieldName]}
          data={inquiry}
          renderValue={fieldName === 'name' && renderNameValue ? renderNameValue : undefined}
          hidePersonalInfo={hidePersonalInfo}
        />
      ))}
      {entries(inquiryReimbursementResultFields()).map(([fieldName, fieldSetting]) => (
        <tr key={fieldName}>
          <th style={{ width: thWidth }}>{fieldSetting.label}</th>
          <td className="text-break" style={{ whiteSpace: 'pre-line' }}>
            {fieldDisplayValue(inquiry.reimbursementResult?.[fieldName], fieldSetting)}
          </td>
        </tr>
      ))}
    </>
  );
}

export function MethodInquiryDetailRows({
  inquiry,
  productsById,
  productTypes,
  products,
  fields,
  country,
  relatedQuestions = [],
  renderNameValue,
  hidePersonalInfo,
  extraRows,
}) {
  const { label: statusLabel, color } = methodInquiryStatuses[inquiry.status] || {};
  return (
    <>
      <tr>
        <th style={{ width: thWidth }}>ID</th>
        <td>{inquiry.id}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>状態</th>
        <td><span className={`badge badge-${color || 'secondary'}`}>{statusLabel}</span></td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>お問合せ日時</th>
        <td>{formatTimestamp(inquiry.createdAt)}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>元受注ID</th>
        <td>{inquiry.sourceOrder?.id}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>元受注日時</th>
        <td>{inquiry.sourceOrder != null && formatTimestamp(inquiry.sourceOrder.createdAt)}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>担当者</th>
        <td>{inquiry.respondedBy?.displayName}</td>
      </tr>
      {extraRows}
      {entries(methodInquiryBasicFields({ productTypes, products })).map(([fieldName, fieldSetting]) => {
        const displayValue = fieldDisplayValue(inquiry[fieldName], fieldSetting);
        const product = fieldName === 'productId' ? productsById?.[inquiry[fieldName]] : null;
        const displayText = product ? `[${product.code}] ${product.name}` : displayValue;
        return (
          <tr key={fieldName}>
            <th style={{ width: thWidth }}>{fieldSetting.label}</th>
            <td className="text-break" style={{ whiteSpace: 'pre-line' }}>{displayText}</td>
          </tr>
        );
      })}
      {relatedQuestions.map((question) => {
        const { id, name } = question;
        const answer = inquiry.answers?.[id];
        return (
          <tr key={id}>
            <th style={{ width: thWidth }}>{name}</th>
            <td>{answerDisplayValue(answer)}</td>
          </tr>
        );
      })}
      {entries(omit(fields, keys(methodInquiryBasicFields({})))).map(([fieldName, fieldSetting]) => (
        <FieldRow
          key={fieldName}
          fieldName={fieldName}
          fieldSetting={fieldSetting}
          value={inquiry[fieldName]}
          data={inquiry}
          renderValue={fieldName === 'name' && renderNameValue ? renderNameValue : undefined}
          hidePersonalInfo={hidePersonalInfo}
        />
      ))}
    </>
  );
}

export function SurveyAnswerDetailRows({
  inquiry,
  survey,
  surveyGroups,
  relatedQuestions = [],
  renderCreatedByValue,
  hidePersonalInfo,
  extraRows,
}) {
  return (
    <>
      {surveyGroups && (
        <tr>
          <th style={{ width: thWidth }}>グループ</th>
          <td>{surveyGroups.find((_) => _.id === survey?.surveyGroupId)?.name}</td>
        </tr>
      )}
      <tr>
        <th style={{ width: thWidth }}>アンケートページ</th>
        <td>{survey?.title}</td>
      </tr>
      <tr>
        <th style={{ width: thWidth }}>回答日時</th>
        <td>{formatTimestamp(inquiry.createdAt)}</td>
      </tr>
      {!hidePersonalInfo && (
        <tr className="personal-info">
          <th style={{ width: thWidth }}>回答者</th>
          <td>{renderCreatedByValue ? renderCreatedByValue(inquiry.createdBy?.displayName) : inquiry.createdBy?.displayName}</td>
        </tr>
      )}
      <tr>
        <th style={{ width: thWidth }}>担当者</th>
        <td>{inquiry.respondedBy?.displayName}</td>
      </tr>
      {extraRows}
      {relatedQuestions.map((question) => {
        const { id, type, name, description } = question;
        const answer = inquiry.answers?.[id];
        const displayValue = {
          text: answer,
          checkbox: keys(answer || {}).join(', '),
          radio: answer,
          select: answer,
          imageFile: [answer].flat().map((a, i) => (
            <div key={i}><a href={a?.url} target="_blank">{a?.name}</a></div>
          )),
        }[type];
        return (
          <tr key={id}>
            <th style={{ width: thWidth }}>
              {name}
              <div className="text-muted small">{description}</div>
            </th>
            <td>{displayValue}</td>
          </tr>
        );
      })}
    </>
  );
}
